'use client'

import { useRef, useState, useEffect, useCallback } from 'react'
import NextImage from 'next/image'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { renderHeading } from '@/utilities/renderHeading'

interface AwardItem {
  url: string
  awardName: string
}

interface AwardsStripProps {
  awards: AwardItem[]
  heading: string | null
}

const INTERVAL_MS = 2000
const TRANSITION_MS = 700

export function AwardsStrip({ awards, heading }: AwardsStripProps) {
  const n = awards.length
  const containerRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const stepRef = useRef(0)
  const [overflows, setOverflows] = useState(false)
  const [step, setStep] = useState(0)
  const [animate, setAnimate] = useState(true)
  const [stepOffset, setStepOffset] = useState(0)
  const [prefersReduced, setPrefersReduced] = useState(false)

  stepRef.current = step

  // Check prefers-reduced-motion
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReduced(mq.matches)
    const handler = (e: MediaQueryListEvent) => setPrefersReduced(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // Measure overflow and the per-card step distance. When the row overflows,
  // the award list is rendered TWICE and the strip wraps around continuously
  // (Steve 2026-08-18): after the first copy has fully scrolled past, the
  // track snaps back one copy-width with the transition off — invisible,
  // because copy two sits exactly where copy one started.
  const measure = useCallback(() => {
    const container = containerRef.current
    const track = trackRef.current
    if (!container || !track) return

    const items = Array.from(track.children) as HTMLElement[]
    if (items.length === 0) return

    const containerWidth = container.offsetWidth
    // Width of ONE copy of the list: start of the duplicate copy, or the whole
    // track when only a single copy is rendered (pre-overflow state).
    const setWidth = items.length > n ? items[n].offsetLeft : track.scrollWidth
    const hasOverflow = setWidth > containerWidth + 2

    setOverflows(hasOverflow)

    if (hasOverflow) {
      const stepSize =
        items.length > 1 ? items[1].offsetLeft - items[0].offsetLeft : items[0].offsetWidth
      if (stepSize <= 0) return
      setStepOffset(stepSize)
    } else {
      setStep(0)
      setStepOffset(0)
    }
  }, [n])

  // Observe container resize
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(container)
    return () => observer.disconnect()
  }, [measure])

  // Wrap-around: once the animation to step n (one full copy) finishes, snap
  // back to step 0 with the transition disabled — visually identical frame.
  useEffect(() => {
    if (step < n || n === 0) return
    const t = setTimeout(() => {
      setAnimate(false)
      setStep((s) => s - n)
    }, TRANSITION_MS + 50)
    return () => clearTimeout(t)
  }, [step, n])

  // Re-enable the transition one frame after a snap
  useEffect(() => {
    if (animate) return
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setAnimate(true)))
    return () => cancelAnimationFrame(id)
  }, [animate])

  const advance = useCallback(() => {
    // Never run past the duplicate copy — the pending snap resets first
    setStep((prev) => (prev >= n ? prev : prev + 1))
  }, [n])

  const retreat = useCallback(() => {
    if (stepRef.current > 0) {
      setStep(stepRef.current - 1)
      return
    }
    // Backwards wrap: snap (no transition) to the same frame one copy in,
    // then animate back one card.
    setAnimate(false)
    setStep(n)
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        setAnimate(true)
        setStep(n - 1)
      }),
    )
  }, [n])

  const startInterval = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    intervalRef.current = setInterval(advance, INTERVAL_MS)
  }, [advance])

  const stopInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  useEffect(() => {
    if (overflows && !prefersReduced) {
      startInterval()
    } else {
      stopInterval()
    }
    return stopInterval
  }, [overflows, prefersReduced, startInterval, stopInterval])

  // Manual controls restart the auto-advance clock so a click is not immediately overridden
  const handlePrev = useCallback(() => {
    retreat()
    if (overflows && !prefersReduced) startInterval()
  }, [retreat, overflows, prefersReduced, startInterval])

  const handleNext = useCallback(() => {
    advance()
    if (overflows && !prefersReduced) startInterval()
  }, [advance, overflows, prefersReduced, startInterval])

  const translateX = overflows ? step * stepOffset : 0
  // Render the list twice when sliding so the wrap always has cards to show
  const renderList = overflows ? [...awards, ...awards] : awards

  return (
    <div className="pt-[var(--spacing-section-sm)] md:pt-[var(--spacing-section-md)]">
      {/* Figma Awards Block 4092:70858 — pt section/md 96, pb section/NONE (436 total =
          96 + 116 heading row + 64 + 160 cards); the next section supplies the gap. */}
      {/* Figma 2272:38871 / 4092:71048 / 2272:38886 — heading + controls stacked above the
          card row. Header→row gap 32/48/64, heading→controls gap 16/24/24. Horizontal
          padding is the responsive --spacing-container token (16/32/64). */}
      {/* Heading + controls stay in the content container; the card row below is
          FULL-BLEED — Figma runs the award tiles to the page edge. */}
      <div className="mx-auto max-w-[var(--container-content)] px-[var(--spacing-container)]">
        <div className="flex flex-col items-start gap-4 md:gap-6">
          {heading && (
            <h2 className="font-display text-display-3xl font-normal text-brand">
              {renderHeading(heading)}
            </h2>
          )}
          {/* icon button 2272:38876 / 2272:38877 — canonical Button, Figma 2198:34803:
              accent fill 36×36 radius sm, hover swaps to theme/secondary, focus ring 3px. */}
          <div className="flex items-start gap-1">
            <Button
              type="button"
              variant="default"
              size="icon"
              aria-label="Previous awards"
              onClick={handlePrev}
            >
              <ChevronLeft className="size-4" aria-hidden="true" />
            </Button>
            <Button
              type="button"
              variant="default"
              size="icon"
              aria-label="Next awards"
              onClick={handleNext}
            >
              <ChevronRight className="size-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </div>

      {/* Card row bleeds to the edge of the CONTENT FRAME (max-w container without the
          side padding) — same treatment as the home-page video band, not full viewport.
          Asana 020 — relative so the feather gradients can pin to the crop edges. */}
      <div
        ref={containerRef}
        className="relative mx-auto mt-8 max-w-[var(--container-content)] overflow-hidden md:mt-12 lg:mt-16"
        onMouseEnter={overflows && !prefersReduced ? stopInterval : undefined}
        onMouseLeave={overflows && !prefersReduced ? startInterval : undefined}
      >
        {/* Asana 020 — feathered edges like the home awards ticker (48px
              background→transparent each side) so tiles never hard-crop. Only
              needed when the row actually overflows and slides. */}
        {overflows && (
          <>
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-background to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-background to-transparent" />
          </>
        )}
        <div
          className={`flex gap-8 md:gap-12 lg:gap-16 ${overflows ? '' : 'justify-center'}`}
          ref={trackRef}
          style={{
            transform: `translateX(-${translateX}px)`,
            transition: animate ? `transform ${TRANSITION_MS}ms ease-in-out` : 'none',
            willChange: overflows ? 'transform' : undefined,
          }}
        >
          {renderList.map((award, i) => (
            /* Award Card 2229:810 — square logo box 100 mobile / 160 tablet+desktop.
                 object-CONTAIN: seal artwork is mixed-ratio (596×800, 323×375, square)
                 and must render as large as possible without edges cropping. */
            <div
              key={i}
              className="flex flex-shrink-0 flex-col items-center gap-4"
              aria-hidden={i >= n || undefined}
            >
              <div className="relative size-25 shrink-0 md:size-40">
                <NextImage
                  src={award.url}
                  alt={i >= n ? '' : award.awardName}
                  fill
                  sizes="(max-width: 767px) 100px, 160px"
                  className="object-contain"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
